/**
 * Клиентский просмотрщик тура: Photo Sphere Viewer 5 + MarkersPlugin + GyroscopePlugin.
 * План этажа — plan-panel.js; попап картины — artwork-modal.js.
 */
import { Viewer } from '@photo-sphere-viewer/core';
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';
import { GyroscopePlugin } from '@photo-sphere-viewer/gyroscope-plugin';

const NAV_MARKER_SIZE = { width: 280, height: 64 };
const INFO_MARKER_SIZE = { width: 420, height: 104 };

/**
 * @returns {object|null}
 */
function readBootstrap() {
  const el = document.getElementById('tour-bootstrap');
  if (!el) {
    return null;
  }
  try {
    return JSON.parse(el.textContent || 'null');
  } catch {
    return null;
  }
}

/**
 * Открыть попап после «Показать в зале» (context from-catalog).
 * Только один раз за загрузку страницы (атрибут снимается).
 */
function maybeOpenFromCatalog() {
  const dialog = document.getElementById('artwork-modal');
  const id = dialog ? (dialog.getAttribute('data-open-artwork-id') || '') : '';
  if (!id) {
    return;
  }
  dialog.removeAttribute('data-open-artwork-id');
  if (window.artworkModal && typeof window.artworkModal.open === 'function') {
    window.artworkModal.open(id, 'from-catalog');
  }
}

/**
 * @returns {string}
 */
function pointIdFromPath() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (parts[0] === 'tour' && parts[1]) {
    return parts[1];
  }
  return '';
}

/**
 * @param {number|string} value
 * @returns {string}
 */
function deg(value) {
  return `${Number(value)}deg`;
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string} mode
 * @returns {'always'|'hover'|'off'}
 */
function captionMode(mode) {
  if (mode === 'hover' || mode === 'off') {
    return mode;
  }
  return 'always';
}

/**
 * @param {'nav'|'art'} kind
 * @param {string} text
 * @param {string} mode
 * @returns {string}
 */
function hotspotHtml(kind, text, mode, extra = {}) {
  const safe = escapeHtml(text);
  const caption = mode === 'off' || !text
    ? ''
    : `<span class="hs__caption">${safe}</span>`;
  const icon = ['bars', 'info', 'dot', 'plus', 'artwork', 'none'].includes(extra.icon)
    ? extra.icon
    : 'bars';
  const mark = `<span class="hs__mark hs__mark--${icon}"></span>`;
  if (kind === 'nav') {
    const thumb = extra.thumbPath
      ? `<span class="hs__thumb" style="background-image:url('${escapeHtml(extra.thumbPath)}')"></span>`
      : '<span class="hs__thumb hs__thumb--empty" aria-hidden="true"></span>';
    return `<div class="hs hs--nav hs--caption-${mode}" data-nav-style="door">
      <span class="hs__pop" aria-hidden="true">
        ${thumb}
        ${caption}
      </span>
      <span class="hs__beacon" aria-hidden="true">
        <span class="hs__ring hs__ring--a"></span>
        <span class="hs__ring hs__ring--b"></span>
        <span class="hs__core"></span>
      </span>
    </div>`;
  }
  if (kind === 'info') {
    return `<div class="hs hs--info hs--caption-${mode}" data-block="info">
      <span class="hs__icon" aria-hidden="true">
        <span class="hs__pulse"></span>
        <span class="hs__disc">
          ${mark}
        </span>
      </span>
      ${caption}
    </div>`;
  }
  if (extra.block === 'task') {
    return `<div class="hs hs--art hs--task hs--caption-${mode}" data-block="task">
      <span class="hs__icon" aria-hidden="true">
        <span class="hs__pulse"></span>
        <span class="hs__disc"><span class="hs__task">?</span></span>
      </span>
      ${caption}
    </div>`;
  }
  return `<div class="hs hs--art hs--caption-${mode}" data-block="interactive">
      <span class="hs__icon" aria-hidden="true">
        <span class="hs__pulse"></span>
        <span class="hs__disc">
          ${mark}
        </span>
      </span>
      ${caption}
    </div>`;
}

/**
 * @param {object} marker
 * @param {string} selector
 * @returns {Element|null}
 */
function markerHotspotEl(marker, selector) {
  const fromConfig = marker.config && marker.config.element
    ? marker.config.element.querySelector(selector)
    : null;
  if (fromConfig) {
    return fromConfig;
  }
  return document.querySelector(`#psv-marker-${marker.id} ${selector}`)
    || document.querySelector(`#psv-marker-${marker.id} .hs`);
}

/**
 * @param {object} point
 * @returns {object[]}
 */
function markersForPoint(point, tourUi = {}) {
  if (!point) {
    return [];
  }
  const icon = tourUi.hotspotIcon || 'bars';
  const hit = Math.max(52, Math.round(Number(tourUi.hotspotSize || 44) * 1.45));
  const artSize = { width: hit, height: hit };
  const infoSize = {
    width: INFO_MARKER_SIZE.width,
    height: Math.max(INFO_MARKER_SIZE.height, hit + 52),
  };
  const nav = (point.navHotspots || []).map((h) => {
    const mode = captionMode(h.captionMode);
    const label = h.label || '';
    const style = h.style === 'floor' ? 'floor' : 'door';
    if (style === 'floor') {
      const size = Math.min(480, Math.max(80, Math.round(Number(h.floorSize) || 240)));
      const color = /^#[0-9a-fA-F]{6}$/.test(h.floorColor || '') ? h.floorColor : '#ffffff';
      const layer = document.createElement('div');
      layer.className = 'hs hs--floor';
      layer.dataset.navStyle = 'floor';
      layer.innerHTML = '<span class="hs__floor-ring" aria-hidden="true"></span>';
      layer.style.width = `${size}px`;
      layer.style.height = `${size}px`;
      const ring = layer.querySelector('.hs__floor-ring');
      if (ring) {
        ring.style.borderColor = color;
        ring.style.borderWidth = `${Math.max(8, Math.round(size * 22 / 240))}px`;
      }
      return {
        id: h.id,
        elementLayer: layer,
        position: { yaw: deg(h.yaw), pitch: deg(h.pitch) },
        size: { width: size, height: size },
        rotation: { pitch: Math.PI / 2 },
        anchor: 'center center',
        hoverScale: false,
        tooltip: mode === 'off' || !label ? null : { content: label, position: 'top center' },
        className: 'psv-marker--hit psv-marker--floor',
        data: { kind: 'nav', targetPointId: h.targetPointId, style: 'floor' },
      };
    }
    return {
      id: h.id,
      position: { yaw: deg(h.yaw), pitch: deg(h.pitch) },
      html: hotspotHtml('nav', label, mode, { thumbPath: h.thumbPath || '', style: 'door' }),
      size: NAV_MARKER_SIZE,
      anchor: 'bottom center',
      hoverScale: false,
      className: 'psv-marker--hit psv-marker--nav',
      data: { kind: 'nav', targetPointId: h.targetPointId, style: 'door' },
    };
  });
  const art = (point.artworkHotspots || []).map((h) => {
    const mode = captionMode(h.captionMode);
    const title = h.text || h.title || '';
    return {
      id: h.id,
      position: { yaw: deg(h.yaw), pitch: deg(h.pitch) },
      html: hotspotHtml('art', title, mode, { icon, block: h.block === 'task' ? 'task' : 'interactive' }),
      size: artSize,
      anchor: 'center left',
      hoverScale: false,
      className: 'psv-marker--hit psv-marker--art',
      data: { kind: 'art', artworkId: h.artworkId },
    };
  });
  const info = [];
  if (
    (point.description || '').trim()
    && Number.isFinite(Number(point.infoYaw))
    && Number.isFinite(Number(point.infoPitch))
  ) {
    info.push({
      id: `info-${point._id}`,
      position: { yaw: deg(point.infoYaw), pitch: deg(point.infoPitch) },
      html: hotspotHtml('info', point.description, 'hover', { icon }),
      size: infoSize,
      anchor: 'center left',
      hoverScale: false,
      className: 'psv-marker--hit psv-marker--info',
      data: { kind: 'info' },
    });
  }
  return nav.concat(art, info);
}

/**
 * Инициализация просмотрщика панорамы.
 */
function init() {
  const bootstrap = readBootstrap();
  if (!bootstrap || !Array.isArray(bootstrap.points)) {
    return;
  }

  const pointsById = new Map(bootstrap.points.map((p) => [p._id, p]));
  const viewerEl = document.getElementById('viewer');
  const unavailableEl = document.getElementById('panorama-unavailable');
  const toastEl = document.getElementById('tour-toast');
  const splashEl = document.getElementById('tour-loader');
  const pointNameEl = document.getElementById('tour-point-name');
  const placeEl = document.getElementById('tour-place');
  const halls = Array.isArray(bootstrap.halls) ? bootstrap.halls : [];
  const tourUi = bootstrap.settings?.tourUi || {};
  const fsBtn = document.getElementById('tour-fullscreen');
  const zoomInBtn = document.getElementById('tour-zoom-in');
  const zoomOutBtn = document.getElementById('tour-zoom-out');
  const gyroBtn = document.getElementById('tour-gyro');

  function hideSplash() {
    if (splashEl) {
      splashEl.classList.add('is-gone');
    }
  }

  function setPointName(point) {
    if (!point) {
      return;
    }
    if (pointNameEl && point.name) {
      pointNameEl.textContent = point.name;
    }
    if (placeEl) {
      const hall = halls.find((item) => item._id === point.hallId);
      const hallName = hall && hall.name ? hall.name : '';
      placeEl.textContent = hallName ? `${hallName} · ${point.name}` : (point.name || '');
    }
  }

  /** @type {Viewer|null} */
  let viewer = null;
  /** @type {MarkersPlugin|null} */
  let markersPlugin = null;
  /** @type {GyroscopePlugin|null} */
  let gyroPlugin = null;
  let gyroSupported = false;
  let gyroBusy = false;
  let currentPointId = pointIdFromPath() || bootstrap.startPointId || '';
  let inFlight = false;
  /** @type {{ id: string, push: boolean }|null} */
  let queued = null;

  function syncZoomControls(zoomLevel = viewer ? viewer.getZoomLevel() : 0) {
    if (zoomOutBtn) {
      zoomOutBtn.disabled = !viewer || zoomLevel <= 0;
    }
    if (zoomInBtn) {
      zoomInBtn.disabled = !viewer || zoomLevel >= 100;
    }
  }

  function syncFullscreenControl(active = viewer ? viewer.isFullscreenEnabled() : false) {
    if (!fsBtn) {
      return;
    }
    fsBtn.disabled = !viewer;
    fsBtn.setAttribute('aria-pressed', String(active));
    fsBtn.setAttribute(
      'aria-label',
      active ? 'Выйти из полноэкранного режима' : 'Перейти в полноэкранный режим',
    );
    fsBtn.title = active ? 'Выйти из полноэкранного режима' : 'На весь экран';
  }

  /**
   * @param {boolean} [enabled]
   */
  function syncGyroControl(enabled = gyroPlugin ? gyroPlugin.isEnabled() : false) {
    if (!gyroBtn) {
      return;
    }
    const canToggle = Boolean(viewer) && gyroSupported && !gyroBusy;
    gyroBtn.disabled = !canToggle;
    gyroBtn.classList.toggle('is-active', enabled);
    gyroBtn.setAttribute('aria-pressed', String(enabled));
    gyroBtn.setAttribute(
      'aria-label',
      enabled ? 'Выключить гироскоп' : 'Включить гироскоп',
    );
    if (!gyroSupported) {
      gyroBtn.title = 'Гироскоп недоступен на этом устройстве';
    } else {
      gyroBtn.title = enabled
        ? 'Ориентация по датчикам включена'
        : 'Смотреть поворотом телефона';
    }
  }

  /**
   * @param {string} message
   */
  function showToast(message) {
    if (!toastEl) {
      return;
    }
    toastEl.textContent = message;
    toastEl.hidden = false;
    window.clearTimeout(showToast._timer);
    showToast._timer = window.setTimeout(() => {
      toastEl.hidden = true;
    }, 2500);
  }

  /**
   * @param {boolean} available
   */
  function setUnavailableVisible(available) {
    if (unavailableEl) {
      unavailableEl.hidden = available;
    }
    if (viewerEl) {
      viewerEl.hidden = !available;
    }
    if (!available) {
      if (zoomOutBtn) zoomOutBtn.disabled = true;
      if (zoomInBtn) zoomInBtn.disabled = true;
      if (fsBtn) fsBtn.disabled = true;
      if (gyroPlugin && gyroPlugin.isEnabled()) {
        gyroPlugin.stop();
      }
      if (gyroBtn) gyroBtn.disabled = true;
    } else if (viewer) {
      syncZoomControls();
      syncFullscreenControl();
      syncGyroControl();
    }
  }

  /**
   * Делает маркеры доступными с клавиатуры и уточняет назначение для скринридера.
   * @param {object} point
   */
  function enhanceMarkerAccessibility(point) {
    if (!viewerEl || !point) {
      return;
    }
    const labels = new Map();
    for (const hotspot of point.navHotspots || []) {
      labels.set(hotspot.id, `Перейти: ${hotspot.label || 'следующая точка'}`);
    }
    for (const hotspot of point.artworkHotspots || []) {
      labels.set(hotspot.id, `Открыть экспонат: ${hotspot.title || 'подробнее'}`);
    }
    if ((point.description || '').trim()) {
      labels.set(`info-${point._id}`, 'Текст этой локации');
    }
    for (const [id, label] of labels) {
      const marker = document.getElementById(`psv-marker-${id}`);
      if (marker) {
        marker.setAttribute('role', 'button');
        marker.setAttribute('tabindex', '0');
        marker.setAttribute('aria-label', label);
      }
    }
  }

  /**
   * @param {object} point
   */
  function applyMarkers(point) {
    if (!markersPlugin) {
      return;
    }
    markersPlugin.setMarkers(markersForPoint(point, tourUi));
    window.requestAnimationFrame(() => enhanceMarkerAccessibility(point));
  }

  /**
   * Синхронизация выделения на плане и мини-карте (в т.ч. §1.5).
   */
  function syncPlanHighlight() {
    if (window.planPanel && typeof window.planPanel.setCurrentPoint === 'function') {
      window.planPanel.setCurrentPoint(currentPointId);
    }
  }

  /**
   * Подгрузка соседних панорам после ready текущей.
   * @param {object} point
   */
  function preloadNeighbors(point) {
    if (!point || !Array.isArray(point.neighborIds)) {
      return;
    }
    for (const nid of point.neighborIds) {
      const n = pointsById.get(nid);
      if (n && n.panoramaAvailable && n.panoramaPath) {
        const img = new Image();
        img.src = n.panoramaPath;
      }
    }
  }

  /**
   * @param {object} point
   * @returns {Promise<void>}
   */
  async function ensureViewer(point) {
    if (viewer) {
      return;
    }
    if (!viewerEl || !point.panoramaPath) {
      throw new Error('Нет контейнера или пути панорамы');
    }

    viewer = new Viewer({
      container: viewerEl,
      panorama: point.panoramaPath,
      navbar: false,
      touchmoveTwoFingers: false,
      plugins: [
        MarkersPlugin.withConfig({
          markers: markersForPoint(point, tourUi),
        }),
        GyroscopePlugin.withConfig({
          roll: true,
          touchmove: true,
          absolutePosition: false,
          moveMode: 'smooth',
        }),
      ],
    });
    markersPlugin = viewer.getPlugin(MarkersPlugin);
    gyroPlugin = viewer.getPlugin(GyroscopePlugin);
    gyroPlugin.isSupported().then((supported) => {
      gyroSupported = Boolean(supported);
      syncGyroControl();
    });
    gyroPlugin.addEventListener('gyroscope-updated', ({ gyroscopeEnabled }) => {
      syncGyroControl(gyroscopeEnabled);
    });
    viewerEl.addEventListener('keydown', (event) => {
      const marker = event.target.closest('.psv-marker--hit');
      if (marker && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        marker.click();
      }
    });
    syncZoomControls();
    syncFullscreenControl();
    syncGyroControl();
    viewer.addEventListener('zoom-updated', ({ zoomLevel }) => {
      syncZoomControls(zoomLevel);
    });
    viewer.addEventListener('fullscreen', ({ fullscreenEnabled }) => {
      syncFullscreenControl(fullscreenEnabled);
    });

    markersPlugin.addEventListener('select-marker', ({ marker }) => {
      const data = marker.data || {};
      if (data.kind === 'nav') {
        const targetId = data.targetPointId;
        const current = pointsById.get(currentPointId);
        const allowed = current && Array.isArray(current.neighborIds)
          && current.neighborIds.includes(targetId);
        if (!allowed) {
          showToast('Переход недоступен');
          return;
        }
        goTo(targetId, { push: true });
        return;
      }
      if (data.kind === 'info') {
        const node = markerHotspotEl(marker, '.hs--info');
        if (node) {
          node.classList.toggle('is-open');
        }
        return;
      }
      if (data.kind === 'art' && data.artworkId) {
        if (window.artworkModal && typeof window.artworkModal.open === 'function') {
          window.artworkModal.open(data.artworkId, 'hotspot');
        }
      }
    });

    await new Promise((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = (err) => {
        cleanup();
        reject(err instanceof Error ? err : new Error('Ошибка загрузки панорамы'));
      };
      const timer = window.setTimeout(() => {
        cleanup();
        // Не блокируем UI навечно: PSV иногда не шлёт ready в embedded WebView
        resolve();
      }, 8000);
      function cleanup() {
        window.clearTimeout(timer);
        viewer.removeEventListener('ready', onReady);
        viewer.removeEventListener('panorama-error', onError);
      }
      viewer.addEventListener('ready', onReady);
      viewer.addEventListener('panorama-error', onError);
    });
    enhanceMarkerAccessibility(point);
    hideSplash();
    maybeOpenFromCatalog();
  }

  /**
   * @param {string} id
   * @param {{ push?: boolean }} [options]
   * @returns {Promise<void>}
   */
  async function doGoTo(id, options = {}) {
    const push = options.push !== false;
    const point = pointsById.get(id);
    if (!point) {
      showToast('Переход недоступен');
      return;
    }

    currentPointId = id;
    setPointName(point);
    window.dispatchEvent(new CustomEvent('tour:point-change', {
      detail: { pointId: id, point },
    }));
    if (push) {
      window.history.pushState({ pointId: id }, '', `/tour/${id}`);
    }

    syncPlanHighlight();
    applyMarkers(point);

    if (!point.panoramaAvailable || !point.panoramaPath) {
      setUnavailableVisible(false);
      return;
    }

    setUnavailableVisible(true);

    try {
      if (!viewer) {
        await ensureViewer(point);
      } else {
        await viewer.setPanorama(point.panoramaPath, {
          position: { yaw: '0deg', pitch: '0deg' },
        });
        applyMarkers(point);
      }
      preloadNeighbors(point);
    } catch {
      setUnavailableVisible(false);
    }
  }

  /**
   * Переход к точке. Очередь длины 1 на время setPanorama.
   * @param {string} id
   * @param {{ push?: boolean }} [options]
   * @returns {Promise<void>}
   */
  async function goTo(id, options = {}) {
    if (inFlight) {
      queued = { id, push: options.push !== false };
      return;
    }
    inFlight = true;
    try {
      await doGoTo(id, options);
    } finally {
      inFlight = false;
      if (queued) {
        const next = queued;
        queued = null;
        await goTo(next.id, { push: next.push });
      }
    }
  }

  window.addEventListener('popstate', (event) => {
    const id = (event.state && event.state.pointId) || pointIdFromPath();
    if (id) {
      goTo(id, { push: false });
    }
  });

  // Экспорт для плана и отладки.
  window.tourViewer = {
    goTo,
    getCurrentPointId: () => currentPointId,
    lookAt: (yawDeg, pitchDeg) => {
      if (viewer && typeof viewer.rotate === 'function') {
        viewer.rotate({ yaw: deg(yawDeg), pitch: deg(pitchDeg) });
      }
    },
    zoomIn: () => {
      if (viewer && typeof viewer.zoomIn === 'function') {
        viewer.zoomIn();
      }
    },
    zoomOut: () => {
      if (viewer && typeof viewer.zoomOut === 'function') {
        viewer.zoomOut();
      }
    },
    toggleFullscreen: () => {
      if (viewer && typeof viewer.toggleFullscreen === 'function') {
        viewer.toggleFullscreen();
      }
    },
    toggleGyro: async () => {
      if (!gyroPlugin || gyroBusy || !gyroSupported) {
        if (!gyroSupported) {
          showToast('Гироскоп недоступен на этом устройстве');
        }
        return;
      }
      if (gyroPlugin.isEnabled()) {
        gyroPlugin.stop();
        return;
      }
      gyroBusy = true;
      syncGyroControl(false);
      try {
        await gyroPlugin.start();
      } catch {
        showToast('Не удалось включить гироскоп. Разрешите доступ к датчикам движения.');
      } finally {
        gyroBusy = false;
        syncGyroControl();
      }
    },
  };

  if (zoomInBtn) {
    zoomInBtn.addEventListener('click', window.tourViewer.zoomIn);
  }
  if (zoomOutBtn) {
    zoomOutBtn.addEventListener('click', window.tourViewer.zoomOut);
  }
  if (fsBtn) {
    fsBtn.addEventListener('click', () => {
      if (window.tourViewer.toggleFullscreen) {
        window.tourViewer.toggleFullscreen();
      }
    });
  }
  if (gyroBtn) {
    gyroBtn.addEventListener('click', () => {
      if (window.tourViewer.toggleGyro) {
        window.tourViewer.toggleGyro();
      }
    });
  }

  const initial = pointsById.get(currentPointId);
  if (!initial) {
    setUnavailableVisible(false);
    syncPlanHighlight();
    return;
  }

  if (!initial.panoramaAvailable || !initial.panoramaPath) {
    setUnavailableVisible(false);
    hideSplash();
    applyMarkers(initial);
    syncPlanHighlight();
    setPointName(initial);
    maybeOpenFromCatalog();
    return;
  }

  setUnavailableVisible(true);
  setPointName(initial);
  ensureViewer(initial)
    .then(() => {
      preloadNeighbors(initial);
      hideSplash();
    })
    .catch(() => {
      setUnavailableVisible(false);
      hideSplash();
      maybeOpenFromCatalog();
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
